<template>
    <div v-if="!displayModalChoixSite" class="container">
        <div class="title-container">
            <h1>
                {{ $t('document.reglementaire.title') }}
                <ReglePastille cle="documentsReglementaires.definition" />
            </h1>
        </div>
        <div class="content-container">
            <div class="filter-container">
                <div class="filter-site-container">
                    {{ $t('intervention.non-preventive.filter') }}
                    <ReglePastille cle="documentsReglementaires.filtresDesactives" />
                    <ButtonBlue
                        v-if="!isUniqueSite"
                        :disabled="(tableData.length === 0 && isLoading) || isAnalyseRisqueOrPriseEnChargeSelected"
                        :on-click="selectionnerSite"
                        :text="$t('bouton.choix.site')"
                        class="button-confirm btn"
                    />
                    <ButtonBlue
                        :disabled="tableData.length === 0 && isLoading"
                        :on-click="selectionnerTypeDocument"
                        :text="$t('bouton.selection.type.documents')"
                        class="button-confirm btn"
                    />
                    <ReglePastille cle="documentsReglementaires.choixTypeObligatoire" />
                </div>
                <Filter
                    :equipementList="equipementListFiltered"
                    :filter="filter"
                    :groupeList="groupeList"
                    :isEquipementDisabled="isAnalyseRisqueOrPriseEnChargeSelected"
                    :isGroupeDisabled="isAnalyseRisqueOrPriseEnChargeSelected"
                    :isSousGroupeDisabled="isAnalyseRisqueOrPriseEnChargeSelected"
                    :searchSite="searchSite"
                    :sousGroupeList="sousGroupeList"
                    class="filter"
                    @addFilterEquipement="addFilterEquipement"
                    @addFilterGroupe="addFilterGroupe"
                    @addFilterSousGroupe="addFilterSousGroupe"
                    @clearFilter="clearFilter"
                    @equipementToggleSelectAll="equipementToggleSelectAll"
                    @groupeToggleSelectAll="groupeToggleSelectAll"
                    @removeFilterEquipement="removeFilterEquipement"
                    @removeFilterGroupe="removeFilterGroupe"
                    @removeFilterSousGroupe="removeFilterSousGroupe"
                    @search="doFilter"
                    @setDocumentDateFrom="setDocumentDateFrom"
                    @setDocumentDateTo="setDocumentDateTo"
                    @siteToggleSelectAll="siteToggleSelectAll"
                    @sousGroupeToggleSelectAll="sousGroupeToggleSelectAll"
                />
            </div>
            <div class="dataTable">
                <div>
                    <h2 tabindex="-1">
                        {{ $tc('document.reglementaire.dataTable.title', +count) }}
                        <ReglePastille cle="documentsReglementaires.portee" />
                    </h2>
                    <ReglePastille v-if="isPriseEnChargeSelected" cle="documentsReglementaires.telechargementIndisponiblePriseEnCharge" />
                    <div v-if="!isPriseEnChargeSelected">
                        <BorderedIconButton
                            v-if="!isDownloadDisabled"
                            :imgUrl="iconeDownload"
                            :title="$t('list.download.selection')"
                            class="button-left"
                            @click="downloadDocuments"
                        />
                        <BorderedIconButton
                            v-if="count > 0"
                            :imgUrl="iconeDownload"
                            :title="$t('list.download.all')"
                            class="button-right"
                            @click="downloadAll"
                        />
                    </div>
                </div>
                <LoaderComponent v-if="tableData.length === 0 && isLoading" class="loader" />
                <div v-else ref="content" class="datatable-container-smaller" @scroll="handleScroll">
                    <DataTable
                        v-if="
                            filter.documentTypeList == null ||
                            filter.documentTypeList.length === 0 ||
                            filter.documentTypeList[0]?.code !== 'ANALYSE_RISQUE' &&
                            filter.documentTypeList[0]?.code !== 'PRISE_EN_CHARGE'
                        "
                        :documentListToDownload="documentListToDownload"
                        :isLoading="isLoading"
                        :noDataText="$t('search.notFound')"
                        :tableData="tableData"
                        :tableHeader="tableHeader"
                        @select="doSelect"
                        @sort="doSort"
                    />
                    <DataTable
                        v-else-if="filter.documentTypeList[0]?.code === 'ANALYSE_RISQUE'"
                        :documentListToDownload="documentListToDownload"
                        :isLoading="isLoading"
                        :noDataText="$t('search.notFound')"
                        :tableData="tableDataAdr"
                        :tableHeader="tableHeaderAdr"
                        @select="doSelectAdr"
                        @sort="doSortAdr"
                    />
                    <DataTable
                        v-else
                        :isLoading="isLoading"
                        :noDataText="$t('search.notFound')"
                        :stopPropaOnPdf="true"
                        :tableData="tableDataPriseEnCharge"
                        :tableHeader="tableHeaderPriseEnCharge"
                        @downloadPdf="downloadPriseEnChargePdf"
                    />
                </div>
            </div>
        </div>
    </div>
    <ModalChoixSite
        v-else
        :is-multi-site="true"
        :inclure-termines="true"
        :selected-site="null"
        :selected-sites="filter.siteList"
        @cancel="modalChoixSiteCancel"
        @valider="validerSite"
    />
    <ModalChoixTypeDocuments
        v-if="displayModalTypeDocument && !isListTypesLoading"
        :selected-types="filter.documentTypeList"
        :types-documents="typeDocumentList"
        @cancel="modaleChoixTypesCancel"
        @valider="validerTypes"
    />
    <ModalDownloadDocuments
        v-if="showModal"
        :documentTypeList="typeDocumentList"
        :nb-documents-modal="nbDocumentsModal"
        @cancel="cancel"
        @downloadDocuments="setFilterForDownloadAllDocuments"
    />
    <ModalChoixNomZip v-if="showModalNomZip" @cancel="showModalNomZip = false" @download="downloadAllDocuments" />
    <WaitingModal v-if="isZipLoading || isListTypesLoading" />
</template>

<script lang="ts">
import { defineComponent, onMounted, ref } from 'vue';
import LoaderComponent from '@/components/ui-components/LoaderComponent.vue';
import DataTable from '@/components/ui-components/DataTable.vue';
import Filter from '@/modules/Common/components/Filter.vue';
import { DocumentReglementaire } from '@/models/DocumentReglementaire';
import { TableHeader } from '@/models/TableHeader';
import { SortType, sortValue } from '@/models/SortType';
import { DocumentReglementaireActions } from '../store/action-types';
import SiteListService, { SiteSearchDTO } from '@/modules/SiteList/service/SiteListService';
import EquipementService from '@/modules/Equipements/services/EquipementService';
import { EquipementFilter } from '@/models/EquipementFilter';
import { DOCUMENT_REGLEMENTAIRE } from '@/modules/DocumentReglementaire/store/mutation-types';
import { Site } from '@/models/Site';
import { Groupe } from '@/models/Groupe';
import BorderedIconButton from '@/components/ui-components/BorderedIconButton.vue';
import {
    DocumentReglementaireToDownload,
    FilterDocumentsReglementaire
} from '@/modules/DocumentReglementaire/services/DocumentReglementaireService';
import ModalChoixSite from '@/modules/Common/components/ModalChoixSite.vue';
import { store } from '@/plugins/store';
import ButtonBlue from '@/components/ui-components/ButtonBlue.vue';
import ModalDownloadDocuments from '@/components/ui-components/ModalDownloadDocuments.vue';
import WaitingModal from '@/components/ui-components/WaitingModal.vue';
import ModalChoixNomZip from '@/components/ui-components/ModalChoixNomZip.vue';
import { SousGroupe } from '@/models/SousGroupe';
import { TypeDocumentReglementaire } from '@/models/TypeDocumentReglementaire';
import ModalChoixTypeDocuments from '@/modules/DocumentReglementaire/pages/ModalChoixTypeDocuments.vue';
import { AnalyseDeRisqueDTO } from '@/models/AnalyseDeRisqueDTO';
import { PriseEnChargeDTO } from '@/models/PriseEnChargeDTO';
import dayjs from 'dayjs';
import ReglePastille from '@/components/regles/ReglePastille.vue';

interface Data {
    siteList: any[];
    documentTypeList: TypeDocumentReglementaire[];
    filter: {
        siteList: any[];
        documentTypeList: TypeDocumentReglementaire[];
        groupeList: any[];
        sousGroupeList: any[];
        equipementList: any[];
        documentDateFrom: string;
        documentDateTo: string;
    };
    siteListTotal: any[];
    groupeList: Groupe[];
    sousGroupeList: SousGroupe[];
    equipementList: EquipementFilter[];
    equipementListFiltered: EquipementFilter[];
    debounce: number;
    searchSite: string;
    documentListToDownload: string[];
    displayModalChoixSite: boolean;
    displayModalTypeDocument: boolean;
    isUniqueSite: boolean;
    showModal: boolean;
    showModalNomZip: boolean;
    documentReglementaireToDownload: DocumentReglementaireToDownload;
    downloadAllFilter: FilterDocumentsReglementaire;
    isDownloadAll: boolean;
    filtreGroupeLoading: boolean;
    filtreEquipementLoading: boolean;
}

export default defineComponent({
    name: 'DocumentReglementairePage',
    components: {
        ModalChoixTypeDocuments,
        ModalChoixNomZip,
        ModalDownloadDocuments,
        Filter,
        DataTable,
        LoaderComponent,
        BorderedIconButton,
        ModalChoixSite,
        ButtonBlue,
        WaitingModal,
        ReglePastille,
    },
    data(): Data {
        return {
            siteList: [],
            documentTypeList: [],
            filter: {
                siteList: [],
                documentTypeList: [],
                groupeList: [],
                sousGroupeList: [],
                equipementList: [],
                documentDateFrom: '',
                documentDateTo: '',
            },
            siteListTotal: [],
            groupeList: [],
            sousGroupeList: [],
            equipementList: [],
            debounce: 0,
            searchSite: '',
            documentListToDownload: [],
            displayModalChoixSite: false,
            displayModalTypeDocument: true,
            isUniqueSite: false,
            showModal: false,
            showModalNomZip: false,
            documentReglementaireToDownload: {},
            downloadAllFilter: {},
            isDownloadAll: false,
            equipementListFiltered: [],
            filtreGroupeLoading: false,
            filtreEquipementLoading: false,
        };
    },
    setup() {
        const content = ref<Element>();

        onMounted(() => {
            content.value;
        });

        return { content };
    },
    created() {
        this.getTypesDocuments();
    },
    async beforeMount() {
        this.filter.siteList = this.filterSite;
        SiteListService.getSiteList(new SiteSearchDTO(0, '', true, true)).then(async res => {
            if (res.data.length === 1) {
                this.isUniqueSite = true;
                this.displayModalChoixSite = false;
            }
            this.siteList = res.data.map(
                ({ id, chantierId, description }: { id: string; chantierId: number; description: string }) => ({
                    id,
                    chantierId,
                    description,
                })
            );

            this.siteListTotal = this.siteList;
            this.$store.commit(`documentReglementaireState/${DOCUMENT_REGLEMENTAIRE.SET_FILTER_SITE}`, this.siteList);

            this.getListGroupBySite();
            this.getEquipementList();
        });
        this.documentTypeList = this.typeDocumentList;
        this.filter.documentTypeList =
            this.filterDocumentType.length > 0 ? this.filterDocumentType : this.documentTypeList;

        this.filter.documentDateFrom = this.filterDocumentDateFrom;
        this.filter.documentDateTo = this.filterDocumentDateTo;

        if (!this.displayModalTypeDocument) {
            this.getDocumentReglementaireData();
        }
        if (this.filterDocumentType.length > 0) {
            this.displayModalTypeDocument = false;
        }
    },
    mounted() {
        this.content?.scrollTo({ top: this.scroll });
    },
    methods: {
        getDocumentReglementaireData(page = 0) {
            page === 0 && this.content?.scrollTo({ top: 0 });

            const isEquipementFilterActive =
                this.filter.equipementList?.length > 0 &&
                this.filter.equipementList.length !== this.equipementListFiltered?.length;

            const ignoreSiteFilter =
                !isEquipementFilterActive &&
                this.filterSite?.length === this.siteList?.length;

            this.$store.dispatch(
                `documentReglementaireState/${DocumentReglementaireActions.GET_DOCUMENT_REGLEMENTAIRE}`,
                {
                    page,
                    sort: this.sort && sortValue(this.sort.sortData, this.sort.sortStatus),
                    adresseProjetId:ignoreSiteFilter
                            ? []
                            : [...new Set(this.filterSite?.map(x => x.id))],
                    chantierIds:ignoreSiteFilter
                            ? []
                            : [...new Set(this.filterSite?.map(x => x.chantierId))],
                    typeDocumentsReglementaireIds:
                        this.filter.documentTypeList?.length === 0
                            ? []
                            : this.filter.documentTypeList
                                  .map(type => type.id)
                                  .filter((id): id is number => id !== null),
                    typeDocumentsReglementaireCodes: this.filter.documentTypeList.map(type => type.code),
                    groupeId:
                        this.filterGroupe?.length === this.groupeList?.length || this.filterGroupe.length > 500
                            ? []
                            : this.filterGroupe?.map(x => x.id),
                    sousGroupeId:
                        this.filterSousGroupe?.length === this.sousGroupeList?.length ||
                        this.filterSousGroupe?.length > 500
                            ? []
                            : this.filterSousGroupe?.map(x => x.id),
                    equipementId:
                        this.equipementListFiltered?.length === this.filter.equipementList?.length
                            ? []
                            : this.filter.equipementList.map(e => e.id),
                    dateDebut: this.filter.documentDateFrom !== '' ? this.filter.documentDateFrom : undefined,
                    dateFin: this.filter.documentDateTo !== '' ? this.filter.documentDateTo : undefined,
                }
            );
        },
        getListGroupBySite() {
            this.filtreGroupeLoading = true;
            const adressesSiteIds: number[] =
                this.filterSite.length !== this.siteListTotal.length ? this.filterSite.map(site => site.id) : [];
            SiteListService.getGroupeListBySites(adressesSiteIds).then(response => {
                this.groupeList = response.data.map(
                    ({ id, libelle, sousGroupes }: { id: number; libelle: string; sousGroupes: SousGroupe[] }) => ({
                        id,
                        libelle,
                        sousGroupes,
                    })
                );
                this.filter.groupeList = this.groupeList.length > 0 ? this.groupeList : [];
                this.$store.commit(
                    `documentReglementaireState/${DOCUMENT_REGLEMENTAIRE.SET_FILTER_GROUPE}`,
                    this.filter.groupeList
                );

                this.sousGroupeList = [];
                response.data.forEach((groupe: Groupe) => {
                    if (groupe.sousGroupes) {
                        groupe.sousGroupes.forEach((sousGroupe: SousGroupe) => {
                            this.sousGroupeList.push(sousGroupe);
                        });
                    }
                });
                this.filter.sousGroupeList = this.sousGroupeList.length > 0 ? this.sousGroupeList : [];
                // mantis 46955 : impossible de sélectionner un site pour filtrer les "Documents règlementaires" :
                this.filtreGroupeLoading = false;
            });
            this.$store.commit(
                `documentReglementaireState/${DOCUMENT_REGLEMENTAIRE.SET_FILTER_SOUS_GROUPE}`,
                this.filter.sousGroupeList
            );
        },
        getEquipementList() {
            this.filtreEquipementLoading = true;
            const adressesSiteIds: number[] =
                this.filterSite.length !== this.siteListTotal.length ? this.filterSite.map(site => site.id) : [];

            EquipementService.getEquipementBySites(adressesSiteIds, '', '').then(async response => {
                this.equipementList = response.data.map(
                    ({
                        id,
                        libelle,
                         phaseLibelle,
                        groupeId,
                        sousGroupeId,
                    }: {
                        id: number;
                        libelle: string;
                        phaseLibelle: string;
                        groupeId: number;
                        sousGroupeId: number;
                    }) => ({
                        id,
                        libelle,
                        phaseLibelle,
                        groupeId,
                        sousGroupeId,
                    })
                );
                this.equipementListFiltered = this.equipementList;

                this.filter.equipementList = this.equipementListFiltered.length > 0 ? this.equipementListFiltered : [];
                this.$store.commit(
                    `documentReglementaireState/${DOCUMENT_REGLEMENTAIRE.SET_FILTER_EQUIPEMENT}`,
                    this.filter.equipementList
                );
                this.filtreEquipementLoading = false;
            });
        },
        doFilter() {
            const equipementId = this.filter.equipementList.map(e => e.id);

            this.$store.commit(`documentReglementaireState/${DOCUMENT_REGLEMENTAIRE.RESET_ITEMS}`);

            this.$store.commit(
                `documentReglementaireState/${DOCUMENT_REGLEMENTAIRE.SET_FILTER_DOCUMENT_TYPE}`,
                this.filter.documentTypeList.length === this.documentTypeList.length ? [] : this.filter.documentTypeList
            );
            this.$store.commit(
                `documentReglementaireState/${DOCUMENT_REGLEMENTAIRE.SET_FILTER_GROUPE}`,
                this.filter.groupeList.length === this.groupeList.length ? [] : this.filter.groupeList
            );
            this.$store.commit(
                `documentReglementaireState/${DOCUMENT_REGLEMENTAIRE.SET_FILTER_SOUS_GROUPE}`,
                this.filter.sousGroupeList.length === this.sousGroupeList.length ? [] : this.filter.sousGroupeList
            );
            this.$store.commit(
                `documentReglementaireState/${DOCUMENT_REGLEMENTAIRE.SET_FILTER_EQUIPEMENT}`,
                equipementId.length === this.equipementList.length ? [] : equipementId
            );
            this.$store.commit(
                `documentReglementaireState/${DOCUMENT_REGLEMENTAIRE.SET_FILTER_DOCUMENT_DATE_FROM}`,
                this.filter.documentDateFrom
            );
            this.$store.commit(
                `documentReglementaireState/${DOCUMENT_REGLEMENTAIRE.SET_FILTER_DOCUMENT_DATE_TO}`,
                this.filter.documentDateTo
            );

            this.getDocumentReglementaireData();
        },
        doSort(sort: { sortData: string; sortStatus: SortType }) {
            const { sortData, sortStatus } = sort;
            this.$store.commit(`documentReglementaireState/${DOCUMENT_REGLEMENTAIRE.SET_SORT}`, {
                sortData,
                sortStatus,
            });
            this.$store.commit(`documentReglementaireState/${DOCUMENT_REGLEMENTAIRE.RESET_ITEMS}`);
            this.getDocumentReglementaireData();
        },
        doSortAdr(sort: { sortData: string; sortStatus: SortType }) {
            const { sortData, sortStatus } = sort;
            this.$store.commit(`documentReglementaireState/${DOCUMENT_REGLEMENTAIRE.SET_SORT_ADR}`, {
                sortData,
                sortStatus,
            });
            this.$store.commit(`documentReglementaireState/${DOCUMENT_REGLEMENTAIRE.RESET_ITEMS}`);
            this.getDocumentReglementaireData();
        },
        doSelect(item: { uniqueDocumentId: string }) {
            if (this.documentListToDownload?.find(element => element === item.uniqueDocumentId)) {
                this.documentListToDownload = this.documentListToDownload.filter(
                    element => element !== item.uniqueDocumentId
                );
            } else {
                this.documentListToDownload?.push(item.uniqueDocumentId);
            }
        },
        doSelectAdr(item: { ficheId: string }) {
            if (this.documentListToDownload?.find(element => element === item.ficheId)) {
                this.documentListToDownload = this.documentListToDownload.filter(element => element !== item.ficheId);
            } else {
                this.documentListToDownload?.push(item.ficheId);
            }
        },
        downloadDocuments() {
            if (this.filter.documentTypeList.length === 1 && this.filter.documentTypeList[0]?.code === 'ANALYSE_RISQUE') {
                this.documentReglementaireToDownload = {
                    uniqueDocumentIds: this.documentListToDownload,
                    nomDocument: 'Analyse_Risque_' + this.documentListToDownload[0] + '.pdf',
                };
                if (this.documentListToDownload.length === 1) {
                    this.$store.dispatch(
                        `documentReglementaireState/${DocumentReglementaireActions.GET_ADR_PDF}`,
                        this.documentReglementaireToDownload
                    );
                    this.documentListToDownload = [];
                } else {
                    this.showModalNomZip = true;
                }
            } else {
                const documentToDownload: DocumentReglementaire[] = this.documentList.filter(document =>
                    this.documentListToDownload.find(element => document.uniqueDocumentId === element)
                );
                this.documentReglementaireToDownload = {
                    uniqueDocumentIds: documentToDownload.map(doc => doc.uniqueDocumentId),
                    chantierIds: documentToDownload.map(doc => doc.chantierId),
                    nomDocument:
                        documentToDownload[0].typeDocumentReglementaireDTO.libelle +
                        '_' +
                        documentToDownload[0].documentId +
                        '_' +
                        documentToDownload[0].indiceFiche,
                };
                if (documentToDownload.length === 1) {
                    this.documentReglementaireToDownload.extension = this.$store.getters[
                        'documentReglementaireState/getDocumentReglementaireItems'
                    ].filter(
                        (doc: DocumentReglementaire) =>
                            this.documentReglementaireToDownload.uniqueDocumentIds !== undefined &&
                            doc.uniqueDocumentId === this.documentReglementaireToDownload.uniqueDocumentIds[0]
                    )[0].extension;

                    this.$store.dispatch(
                        `documentReglementaireState/${DocumentReglementaireActions.GET_DOCUMENT_REGLEMENTAIRE_PDF}`,
                        this.documentReglementaireToDownload
                    );
                    this.documentListToDownload = [];
                } else {
                    this.showModalNomZip = true;
                }
                this.isDownloadAll = false;
            }
        },
        async downloadAllDocuments(nomZip: string) {
            if (this.isDownloadAll) {
                this.downloadAllFilter.nomZip = nomZip;
                if (this.filter.documentTypeList.length === 1 && this.filter.documentTypeList[0]?.code === 'ANALYSE_RISQUE') {
                    await this.$store.dispatch(
                        `documentReglementaireState/${DocumentReglementaireActions.GET_ALL_ADR_PDF}`,
                        this.downloadAllFilter
                    );
                } else {
                    await this.$store.dispatch(
                        `documentReglementaireState/${DocumentReglementaireActions.GET_ALL_DOCUMENT_REGLEMENTAIRE_PDF}`,
                        this.downloadAllFilter
                    );
                }
            } else {
                this.documentReglementaireToDownload.nomZip = nomZip;
                if (this.filter.documentTypeList.length === 1 && this.filter.documentTypeList[0]?.code === 'ANALYSE_RISQUE') {
                    await this.$store.dispatch(
                        `documentReglementaireState/${DocumentReglementaireActions.GET_ADR_PDF}`,
                        this.documentReglementaireToDownload
                    );
                } else {
                    await this.$store.dispatch(
                        `documentReglementaireState/${DocumentReglementaireActions.GET_DOCUMENT_REGLEMENTAIRE_PDF}`,
                        this.documentReglementaireToDownload
                    );
                }
                this.documentListToDownload = [];
            }
            this.showModalNomZip = false;
        },
        setFilterForDownloadAllDocuments(filterDoc: {
            documentDateFrom: string;
            documentDateTo: string;
            documentTypeList: TypeDocumentReglementaire[];
        }): void {
            this.$store.commit(`documentReglementaireState/${DOCUMENT_REGLEMENTAIRE.SET_ZIP_IS_LOADING}`, true);
            this.downloadAllFilter = {
                dateDebut: filterDoc.documentDateFrom,
                dateFin: filterDoc.documentDateTo,
                typeDocumentsReglementaireIds: filterDoc.documentTypeList
                    .map(typeDocument => typeDocument.id)
                    .filter((id): id is number => id !== null),
                chantierIds:
                    this.siteList.length === this.filterSite.length ? [] : this.filterSite.map(site => site.chantierId),
                adresseProjetId:
                    this.filterSite?.length === this.siteList?.length
                        ? []
                        : [...new Set(this.filterSite?.map(x => x.id))],
                isForModal: true,
                size: 1,
            };
            this.$store
                .dispatch(
                    `documentReglementaireState/${DocumentReglementaireActions.GET_DOCUMENT_REGLEMENTAIRE}`,
                    this.downloadAllFilter
                )
                .then(() => {
                    this.$store.commit(
                        `documentReglementaireState/${DOCUMENT_REGLEMENTAIRE.SET_ZIP_IS_LOADING}`,
                        false
                    );
                    if (this.nbDocumentsModal < 100) {
                        this.showModal = false;
                        this.showModalNomZip = true;
                    }
                });
        },
        getTypesDocuments() {
            this.$store.dispatch(
                `documentReglementaireState/${DocumentReglementaireActions.GET_TYPES_DOCUMENTS_REGLEMENTAIRES}`,
                { isBim: false }
            );
        },
        setDocumentDateFrom(value: string): void {
            this.filter.documentDateFrom = value;
        },
        setDocumentDateTo(value: string): void {
            this.filter.documentDateTo = value;
        },
        addFilterGroupe(groupe: Groupe) {
            this.filter.groupeList?.push(groupe);
            this.refreshSousGroupe();
            this.refreshEquipement();
        },
        removeFilterGroupe(groupe: Groupe) {
            this.filter.groupeList = this.filter.groupeList?.filter(groupeData => groupeData !== groupe);
            this.refreshSousGroupe();
            this.refreshEquipement();
        },
        addFilterSousGroupe(sousGroupe: SousGroupe) {
            this.filter.sousGroupeList?.push(sousGroupe);
            this.refreshEquipementBySousGroupe();
        },
        removeFilterSousGroupe(sousGroupe: SousGroupe) {
            this.filter.sousGroupeList = this.filter.sousGroupeList?.filter(
                sousGroupeData => sousGroupeData !== sousGroupe
            );
            this.refreshEquipementBySousGroupe();
        },
        addFilterEquipement(equipement: EquipementFilter) {
            this.filter.equipementList.push(equipement);
        },
        removeFilterEquipement(equipement: EquipementFilter) {
            this.filter.equipementList = this.filter.equipementList.filter(
                equipementItem => equipementItem !== equipement
            );
        },
        equipementToggleSelectAll() {
            this.filter.equipementList =
                this.filter.equipementList.length === this.equipementListFiltered.length
                    ? []
                    : this.equipementListFiltered;
        },
        clearFilter() {
            this.filter.siteList = this.siteList ?? [];
            this.filter.groupeList = this.groupeList ?? [];
            this.filter.sousGroupeList = this.sousGroupeList ?? [];
            this.filter.equipementList = this.equipementList ?? [];
        },
        siteToggleSelectAll() {
            this.filter.siteList = this.filter.siteList.length === this.siteList.length ? [] : this.siteList;
            this.getListGroupBySite();
            this.getEquipementList();
        },
        groupeToggleSelectAll() {
            this.filter.groupeList = this.filter.groupeList.length === this.groupeList.length ? [] : this.groupeList;
            this.refreshSousGroupe();
            this.refreshEquipement();
        },
        sousGroupeToggleSelectAll() {
            this.filter.sousGroupeList =
                this.filter.sousGroupeList.length === this.sousGroupeList.length ? [] : this.sousGroupeList;
            this.refreshEquipement();
        },
        refreshSousGroupe() {
            this.sousGroupeList = [];
            this.filter.groupeList.forEach(groupe => {
                if (groupe.sousGroupes != null) {
                    groupe.sousGroupes.forEach((sousGroupe: SousGroupe) => this.sousGroupeList.push(sousGroupe));
                }
            });
            this.filter.sousGroupeList = this.sousGroupeList;
            this.sousGroupeList.sort();
        },
        refreshEquipement() {
            this.equipementListFiltered = [];
            this.filter.groupeList.forEach(groupe => {
                const equipements = this.equipementList.filter(equipement => equipement.groupeId === groupe.id);
                if (equipements != null) {
                    equipements.forEach(equipement => this.equipementListFiltered.push(equipement));
                }
            });
            this.filter.equipementList = this.equipementListFiltered;
        },
        refreshEquipementBySousGroupe() {
            this.equipementListFiltered = [];
            this.filter.sousGroupeList.forEach(sousGroupe => {
                const equipements = this.equipementList.filter(equipement => equipement.sousGroupeId === sousGroupe.id);
                if (equipements != null) {
                    equipements.forEach(equipement => this.equipementListFiltered.push(equipement));
                }
            });
            this.filter.equipementList = this.equipementListFiltered;
        },
        handleScroll() {
            const top = this.content?.scrollTop ?? 0;
            const max = this.content?.scrollHeight ?? 0;
            const height = this.content?.clientHeight ?? 0;
            if (max - top <= height * 1.5 && !this.isLoading && this.page + 1 < this.numberOfPages) {
                this.$store.commit(`documentReglementaireState/${DOCUMENT_REGLEMENTAIRE.SET_PAGE}`, this.page + 1);
                this.getDocumentReglementaireData(this.page);
            }
        },
        validerSite(sites: Site[]): void {
            this.displayModalChoixSite = false;
            store.commit(`documentReglementaireState/${DOCUMENT_REGLEMENTAIRE.SET_FILTER_SITE}`, sites);
            store.commit(`documentReglementaireState/${DOCUMENT_REGLEMENTAIRE.RESET_ITEMS}`);
            this.getListGroupBySite();
            this.getEquipementList();
            this.getDocumentReglementaireData();
        },
        validerTypes(types: TypeDocumentReglementaire[]): void {
            this.displayModalTypeDocument = false;
            this.filter.documentTypeList = types;
            this.$store.commit(`documentReglementaireState/${DOCUMENT_REGLEMENTAIRE.RESET_ITEMS}`);
            this.$store.commit(
                `documentReglementaireState/${DOCUMENT_REGLEMENTAIRE.SET_FILTER_DOCUMENT_TYPE}`,
                this.filter.documentTypeList.length === this.documentTypeList.length ? [] : this.filter.documentTypeList
            );
            this.sort = { sortStatus: SortType.DESC, sortData: 'dateIntervention' };
            if (this.filter.documentTypeList.length === 1 && this.filter.documentTypeList[0]?.code === 'ANALYSE_RISQUE') {
                const sort: { sortData: string; sortStatus: SortType } = {
                    sortData: 'dateCreation',
                    sortStatus: SortType.DESC,
                };
                const { sortData, sortStatus } = sort;
                this.$store.commit(`documentReglementaireState/${DOCUMENT_REGLEMENTAIRE.SET_SORT}`, {
                    sortData,
                    sortStatus,
                });
            } else {
                this.$store.commit(`documentReglementaireState/${DOCUMENT_REGLEMENTAIRE.SET_SORT}`, {
                    sortStatus: SortType.DESC,
                    sortData: 'dateIntervention',
                });
            }
            this.getDocumentReglementaireData();
        },
        modalChoixSiteCancel(): void {
            this.siteList = this.siteListTotal;
            this.displayModalChoixSite = false;
            if (this.tableData.length === 0 && !this.isLoading) {
                store.commit(`documentReglementaireState/${DOCUMENT_REGLEMENTAIRE.SET_FILTER_SITE}`, this.siteList);
                store.commit(`documentReglementaireState/${DOCUMENT_REGLEMENTAIRE.RESET_ITEMS}`);
                this.getListGroupBySite();
                this.getEquipementList();
                this.getDocumentReglementaireData();
            }
        },
        modaleChoixTypesCancel(): void {
            this.displayModalTypeDocument = false;
        },
        selectionnerSite(): void {
            this.displayModalChoixSite = true;
        },
        selectionnerTypeDocument(): void {
            this.displayModalTypeDocument = true;
            this.documentListToDownload = [];
        },
        cancel() {
            this.showModal = false;
        },
        downloadAll() {
            this.isDownloadAll = true;
            if (this.count < 100) {
                this.downloadAllFilter = {
                    dateDebut: this.filter.documentDateFrom,
                    dateFin: this.filter.documentDateTo,
                    typeDocumentsReglementaireIds: this.filter.documentTypeList
                        .map(typeDocument => typeDocument.id)
                        .filter((id): id is number => id !== null),
                    adresseProjetId:
                        this.filterSite?.length === this.siteList?.length
                            ? []
                            : [...new Set(this.filterSite?.map(x => x.id))],
                    chantierIds:
                        this.siteList.length === this.filterSite.length
                            ? []
                            : this.filterSite.map(site => site.chantierId),
                };
                this.showModalNomZip = true;
            } else {
                this.showModal = true;
            }
        },
        downloadPriseEnChargePdf(item: any): void {
            const { idDocExterne, idVersionExterne } = item;

            this.$store.dispatch(
                `documentReglementaireState/${DocumentReglementaireActions.GET_PRISE_EN_CHARGE_PDF}`,
                { idDocExterne, idVersionExterne }
            );
        }
    },
    computed: {
        tableData(): DocumentReglementaire[] {
            return this.$store.getters['documentReglementaireState/getDocumentReglementaireItems'].map(
                ({
                    uniqueDocumentId,
                    interventionId,
                    indiceFiche,
                    typeDocumentReglementaireDTO,
                    adresseProjetDescription,
                    groupeLibelle,
                    sousGroupeLibelle,
                    libelleEquipement,
                    phaseLibelle,
                    dateIntervention,
                    nomDocument,
                }: DocumentReglementaire) => {
                    return {
                        uniqueDocumentId: uniqueDocumentId,
                        interventionId:
                            typeDocumentReglementaireDTO.id !== null && typeDocumentReglementaireDTO.id > 9
                                ? interventionId + '-' + nomDocument
                                : indiceFiche
                                ? `${interventionId}-${indiceFiche}`
                                : interventionId,
                        typesDocumentReglementaire: typeDocumentReglementaireDTO.libelle,
                        adresseProjetDescription: adresseProjetDescription,
                        groupeLibelle: groupeLibelle,
                        sousGroupeLibelle: sousGroupeLibelle,
                        libelleEquipement: {
                            libelle: libelleEquipement,
                            phase: phaseLibelle && phaseLibelle.trim().toLowerCase() !== 'maintenance' ? phaseLibelle : undefined,
                        },
                        dateIntervention: dateIntervention ? new Date(dateIntervention).toLocaleDateString() : '',
                    };
                }
            );
        },
        tableDataAdr(): AnalyseDeRisqueDTO[] {
            return this.$store.getters['documentReglementaireState/getAdrItems'].map(
                ({ id, libelleSite, dateCreation, dateDebut, dateFin }: AnalyseDeRisqueDTO) => {
                    return {
                        uniqueDocumentId: id,
                        ficheId: id,
                        libelleSite: libelleSite,
                        dateCreation: dateCreation ? new Date(dateCreation).toLocaleDateString() : '',
                        dateDebut: dateDebut ? new Date(dateDebut).toLocaleDateString() : '',
                        dateFin: dateFin ? new Date(dateFin).toLocaleDateString() : '',
                    };
                }
            );
        },
        tableDataPriseEnCharge(): PriseEnChargeDTO[] {
            return this.$store.getters['documentReglementaireState/getPriseEnChargeItems'].map(
                ({ id, idDocExterne, idVersionExterne, titre, libelleChantier, dateImport, dateModif }: PriseEnChargeDTO) => ({
                    idMasque: id,
                    libelle: titre,
                    projet: libelleChantier,
                    dateMiseAJour: (dateModif || dateImport) ? dayjs(dateModif ?? dateImport).format('DD/MM/YYYY') : '',
                    pdfExist: true,
                    idDocExterne,
                    idVersionExterne
                })
            );
        },
        tableHeader(): TableHeader[] {
            return this.$store.getters['documentReglementaireState/getDocumentReglementaireTableHeader'];
        },
        tableHeaderAdr(): TableHeader[] {
            return this.$store.getters['documentReglementaireState/getAdrTableHeader'];
        },
        tableHeaderPriseEnCharge(): TableHeader[] {
            return this.$store.getters['documentReglementaireState/getPecTableHeader'];
        },
        isPriseEnChargeSelected(): boolean {
            return this.filter.documentTypeList.length === 1 &&
                this.filter.documentTypeList[0]?.code === 'PRISE_EN_CHARGE';
        },
        isAnalyseRisqueSelected(): boolean {
            return this.filter.documentTypeList.length === 1 &&
                this.filter.documentTypeList[0]?.code === 'ANALYSE_RISQUE';
        },
        isAnalyseRisqueOrPriseEnChargeSelected(): boolean {
            return this.isAnalyseRisqueSelected || this.isPriseEnChargeSelected;
        },
        typeDocumentList(): TypeDocumentReglementaire[] {
            return this.$store.getters['documentReglementaireState/getTypesDocumentsReglementaires'];
        },
        documentList(): DocumentReglementaire[] {
            return this.$store.getters['documentReglementaireState/getDocumentReglementaireItems'];
        },
        isLoading() {
            return this.$store.getters['documentReglementaireState/getDocumentReglementaireIsLoading'];
        },
        page() {
            return this.$store.getters['documentReglementaireState/getDocumentReglementairePage'];
        },
        numberOfPages() {
            return this.$store.getters['documentReglementaireState/getDocumentReglementaireNbOfPages'];
        },
        scroll() {
            return this.$store.getters['documentReglementaireState/getDocumentReglementaireScroll'];
        },
        filterSite(): any[] {
            return this.$store.getters['documentReglementaireState/getDocumentReglementaireFilterSite'];
        },
        filterDocumentType(): any[] {
            return this.$store.getters['documentReglementaireState/getDocumentReglementaireFilterDocumentType'];
        },
        filterGroupe(): any[] {
            return this.$store.getters['documentReglementaireState/getDocumentReglementaireFilterGroupe'];
        },
        filterSousGroupe(): any[] {
            return this.$store.getters['documentReglementaireState/getDocumentReglementaireFilterSousGroupe'];
        },
        filterEquipement(): any[] {
            return this.$store.getters['documentReglementaireState/getDocumentReglementaireFilterEquipement'];
        },
        filterDocumentDateFrom() {
            return this.$store.getters['documentReglementaireState/getDocumentReglementaireFilterDocumentDateFrom'];
        },
        filterDocumentDateTo() {
            return this.$store.getters['documentReglementaireState/getDocumentReglementaireFilterDocumentDateTo'];
        },
        isDownloadDisabled(): boolean {
            if (this.isPriseEnChargeSelected) return true;
            if (this.filter.documentTypeList.length === 1 && this.filter.documentTypeList[0]?.code === 'ANALYSE_RISQUE') {
                return this.tableDataAdr.length === 0 || this.documentListToDownload.length === 0;
            } else {
                return this.tableData.length === 0 || this.documentListToDownload.length === 0;
            }
        },
        count() {
            return this.$store.getters['documentReglementaireState/getDocumentReglementaireCount'];
        },
        sort(): {
            sortData: string;
            sortStatus: SortType;
        } {
            return this.$store.getters['documentReglementaireState/getDocumentReglementaireSort'];
        },
        iconeDownload() {
            return require('@/assets/img/pdf-icon.svg');
        },
        closeIcon() {
            return require('@/assets/img/close-white.svg');
        },
        isZipLoading(): boolean {
            return this.$store.getters['documentReglementaireState/getZipIsLoading'];
        },
        isListTypesLoading(): boolean {
            return this.$store.getters['documentReglementaireState/getListTypesIsLoading'];
        },
        nbDocumentsModal(): number {
            return this.$store.getters['documentReglementaireState/getNombreDocumentModal'];
        },
    },
});
</script>

<style lang="scss" scoped>
@import '@/theme/pages-recherche.scss';

@media screen and (max-width: 1631px) {
    .datatable-container {
        max-height: calc(100vh - 340px);
    }
}

@media screen and (max-width: 1536px) {
    .datatable-container {
        max-height: calc(100vh - 440px);
    }
}

@media screen and (max-width: 970px) {
    .datatable-container {
        max-height: calc(100vh - 518px);
    }
}

:deep(.containerBtn) {
    height: 20px;
}
:deep(.icon) {
    height: 20px;
}
.filter-container {
    display: flex;
    padding: 8px 16px;
    background: white;
    border-radius: 6px;
    margin-bottom: 8px;
}
.filter-site-container {
    flex-direction: column;
    width: 15%;
}
.filter {
    width: 100%;
}
.button-confirm {
    right: 16px;
}
.btn {
    width: 100%;
    border-radius: 5px;
    cursor: pointer;
    height: 40px;
    margin-top: 20px;
}
:deep(.filter) {
    margin-bottom: 0;
    padding: 0 16px;
}
.button-left {
    float: left;
    margin-right: 10px;
    height: 28px;
}
.button-right {
    float: right;
    height: 28px;
}
.title-container h1 {
    display: inline;
    font-size: inherit;
    font-weight: inherit;
    margin: 0;
}
</style>
